import TabsContainer, { TabT } from 'components/Tabs/Container'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import { Fields as RedeemTemplate } from 'models/RedeemTemplate'
import React, { useEffect, useMemo, useState } from 'react'
import { Container } from 'react-bootstrap-five'
import Tab from 'react-bootstrap-five/Tab'
import { I18n } from 'react-redux-i18n'
import RewardExpiration from 'scenes/Cms/Settings/RewardExpiration'
import { history } from 'store'
import { useAbility } from 'utilities/ability'
import TabPane from './TabPane'

type PropsT = {
  activeTemplates: RedeemTemplate[]
  draftTemplates: RedeemTemplate[]
  archivedTemplates: RedeemTemplate[]
}

const settingsT = buildTranslate('cms.settings.reward_expiration')

// Pure wrapper over I18n.t — kept at module scope (not in the component body)
// so it isn't a fresh reference on every render and can be referenced from
// useMemo without tripping exhaustive-deps.
const getName = (state: string): string =>
  I18n.t(`redeem_manager.landing.tabs.${state}.title`)

// Resolve the tab a URL hash points at (e.g. `/rewards#settings`), falling
// back to 'published' when the hash isn't a rendered tab.
const tabFromHash = (validTabs: string[], hash: string): string => {
  const normalized = hash.replace(/^#/, '')
  return validTabs.includes(normalized) ? normalized : 'published'
}

const RedeemTemplateTabs = ({
  activeTemplates,
  draftTemplates,
  archivedTemplates,
}: PropsT) => {
  const grantedRewardExpirationEnabled = useFlag(
    'mx-granted-reward-expiration-settings',
    false
  )
  const canViewMerchantSettings = useAbility().can('access', 'MerchantSettings')
  const showSettingsTab =
    grantedRewardExpirationEnabled && canViewMerchantSettings

  // Track the URL hash reactively so the active tab follows in-page hash
  // navigation (e.g. clicking "Reward Settings" while already on /rewards),
  // not just the initial mount. React Router doesn't remount this component on
  // a hash-only change. The nav link navigates via `history.push`, which does
  // NOT fire the browser `hashchange` event — so we subscribe to the router
  // history directly (the same instance ConnectedRouter and the NavLinks push
  // to). `history.listen` fires on push/replace/pop, covering the nav-link
  // click, direct deep-links, and back/forward in one path.
  const [activeHash, setActiveHash] = useState(() => history.location.hash)
  useEffect(() => {
    setActiveHash(history.location.hash)
    return history.listen(location => setActiveHash(location.hash))
  }, [])

  // The valid tab set only changes with the feature flag / MerchantSettings
  // permission (via showSettingsTab) and the template arrays, so memoize to
  // avoid rebuilding the tab JSX on every render.
  const tabs: TabT[] = useMemo(() => {
    const baseTabs: TabT[] = [
      {
        id: 'published',
        name: getName('published'),
        content: (
          <TabPane
            key="published"
            state="published"
            templates={activeTemplates}
          />
        ),
      },
      {
        id: 'unpublished',
        name: getName('unpublished'),
        content: (
          <TabPane
            key="unpublished"
            state="unpublished"
            templates={draftTemplates}
          />
        ),
      },
      {
        id: 'archived',
        name: getName('archived'),
        content: (
          <TabPane
            key="archived"
            state="archived"
            templates={archivedTemplates}
          />
        ),
      },
    ]

    if (showSettingsTab) {
      baseTabs.push({
        id: 'settings',
        name: settingsT('tab_title'),
        // Wrapped in <Tab.Pane> like the sibling tabs so eventKey-based
        // active/inactive visibility works.
        content: (
          <Tab.Pane
            key="settings"
            className="py-l grey-05-bkg"
            eventKey="settings"
          >
            {/* Container gives CmsPage's Row/Col the tabbed-page width/gutter. */}
            <Container>
              <RewardExpiration showTitle={false} />
            </Container>
          </Tab.Pane>
        ),
      })
    }

    return baseTabs
  }, [showSettingsTab, activeTemplates, draftTemplates, archivedTemplates])

  // Resolve the active tab from the current hash. Recomputed when the hash or
  // the tab set changes; keying TabsContainer on it remounts the tabs so the
  // selection follows hash navigation (TabsContainer reads defaultTab only as
  // its initial state, so it would otherwise ignore later changes).
  const defaultTab = useMemo(
    () =>
      tabFromHash(
        tabs.map(t => t.id),
        activeHash
      ),
    [tabs, activeHash]
  )

  // When a tab is deep-linked via the URL hash (e.g. `/rewards#settings`), the
  // tab is auto-selected but keyboard focus stays at the top of the page. Move
  // focus to the active tab so keyboard users land on the content they
  // navigated to (WCAG 2.4.3). Re-runs on hash navigation, not just initial
  // mount. This lives here rather than in the parent Landing scene because the
  // Spinner unmounts/remounts these tabs while reward data loads — an effect in
  // Landing would fire before the tabs exist and have its focus wiped by the
  // remount.
  useEffect(() => {
    if (!activeHash) return
    const selectedTab = document.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]'
    )
    selectedTab?.focus()
  }, [activeHash, defaultTab])

  return (
    <TabsContainer
      // Keyed on the resolved tab so a hash change remounts the tabs with the
      // new selection (see defaultTab above). This also covers the granted-
      // reward nag CTA (`push('/rewards#settings')`) when the user is already
      // on /rewards.
      key={defaultTab}
      defaultTab={defaultTab}
      // shouldReplaceHash omitted: writing the hash back triggers a scroll
      // that pushes the nag banner out of view on every tab click.
      tabs={tabs}
    />
  )
}

export default RedeemTemplateTabs
